/**
 * Atomic UI primitives for staff portals (CRM, Dispatch, Tech).
 * Built on Brad Frost atomic design — every reusable piece lives here.
 * Designed for professional B2B SaaS aesthetics, not retro/marketing.
 *
 * Palette (strict): Navy #002060 + Orange #FF6600 + White #FFFFFF. No greys.
 */
import { useEffect, useState } from "react";
import { Search, Download, Filter as FilterIcon, X, ChevronDown, Inbox, Menu } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// ============== Shell ==============
export function PortalShell({ children }) {
  return <div className="min-h-screen bg-[#FFFFFF] text-[#002060]">{children}</div>;
}

export function TopBar({ title, subtitle, brand, user, onLogout, right, onMenu }) {
  return (
    <header className="sticky top-0 z-30 bg-[#FFFFFF] border-b border-[#002060]/15">
      <div className="px-3 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {onMenu && (
            <button
              type="button"
              onClick={onMenu}
              data-testid="portal-menu-btn"
              aria-label="Open menu"
              className="lg:hidden w-10 h-10 grid place-items-center rounded-md hover:bg-[#FF6600]/10 text-[#002060] transition shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}
          {brand}
          <div className="hidden sm:block min-w-0">
            <div className="display text-sm font-semibold tracking-tight truncate">{title}</div>
            {subtitle && <div className="text-[11px] text-[#002060]/60 truncate">{subtitle}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {right}
          {user && (
            <div className="hidden md:flex items-center gap-2 text-xs text-[#002060]/60">
              <span className="w-7 h-7 rounded-full bg-[#FF6600]/10 grid place-items-center font-semibold text-[#FF6600] text-[10px]">
                {(user.email || "?").slice(0, 2).toUpperCase()}
              </span>
              <span className="truncate max-w-[180px]">{user.email}</span>
            </div>
          )}
          {onLogout && (
            <Button data-testid="portal-logout" variant="outline" size="sm" onClick={onLogout} className="border-[#002060]/30 text-[#002060] h-9">
              Sign out
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

// Shared nav list (used by Sidebar + MobileDrawer)
function NavList({ items, active, onSelect, testidPrefix = "sidebar" }) {
  return (
    <nav className="p-3 space-y-0.5">
      {items.map((it) => {
        const Icon = it.icon;
        const isActive = it.key === active;
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onSelect(it.key)}
            data-testid={`${testidPrefix}-${it.key}`}
            className={`w-full text-left px-3 py-2.5 rounded-md text-sm flex items-center gap-2.5 transition-colors ${
              isActive ? "bg-[#FF6600]/10 text-[#FF6600] font-medium" : "text-[#002060] hover:bg-[#FF6600]/10"
            }`}
          >
            {Icon && <Icon className={`w-4 h-4 ${isActive ? "text-[#FF6600]" : "text-[#002060]/40"}`} />}
            <span className="truncate">{it.label}</span>
            {it.badge != null && (
              <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${isActive ? "bg-[#FF6600]/15 text-[#FF6600]" : "bg-[#002060]/5 text-[#002060]/70"}`}>
                {it.badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

export function Sidebar({ items, active, onSelect }) {
  return (
    <aside className="hidden lg:flex flex-col w-56 shrink-0 bg-[#FFFFFF] border-r border-[#002060]/15 sticky top-14 self-start overflow-y-auto" style={{ height: "calc(100vh - 56px)" }}>
      <NavList items={items} active={active} onSelect={onSelect} testidPrefix="sidebar" />
    </aside>
  );
}

/**
 * Mobile slide-out drawer. Controlled.
 *  - `open` boolean
 *  - `onClose()`
 *  - same `items` / `active` / `onSelect` as Sidebar
 */
export function MobileDrawer({ open, onClose, items, active, onSelect, brand, user, onLogout }) {
  // ESC + body-scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const h = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", h); };
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-[#002060]/70 backdrop-blur-sm transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <aside
        data-testid="portal-mobile-drawer"
        className={`lg:hidden fixed top-0 left-0 bottom-0 z-50 w-[80vw] max-w-xs bg-[#FFFFFF] shadow-2xl flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!open}
      >
        <header className="h-14 px-4 flex items-center justify-between border-b border-[#002060]/15 shrink-0">
          {brand}
          <button
            type="button"
            onClick={onClose}
            data-testid="portal-mobile-close"
            aria-label="Close menu"
            className="w-9 h-9 grid place-items-center rounded-md hover:bg-[#FF6600]/10 text-[#002060]/70 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          <NavList
            items={items}
            active={active}
            onSelect={(k) => { onSelect(k); onClose(); }}
            testidPrefix="mobnav"
          />
        </div>
        {(user || onLogout) && (
          <footer className="border-t border-[#002060]/15 p-3 space-y-2 shrink-0">
            {user && (
              <div className="flex items-center gap-2 text-xs text-[#002060]/70">
                <span className="w-7 h-7 rounded-full bg-[#FF6600]/10 grid place-items-center font-semibold text-[#FF6600] text-[10px]">
                  {(user.email || "?").slice(0, 2).toUpperCase()}
                </span>
                <span className="truncate">{user.email}</span>
              </div>
            )}
            {onLogout && (
              <Button data-testid="portal-mobile-logout" variant="outline" size="sm" onClick={onLogout} className="w-full border-[#002060]/30 text-[#002060] h-9">
                Sign out
              </Button>
            )}
          </footer>
        )}
      </aside>
    </>
  );
}

/** Lightweight controller hook so portals can wire up the drawer in one line. */
export function useMobileDrawer() {
  const [open, setOpen] = useState(false);
  return { open, openMenu: () => setOpen(true), closeMenu: () => setOpen(false) };
}

/** Legacy alias — preserved so existing call sites keep working (no-op chip strip if used). */
export function MobileTabs() { return null; }

export function PageContent({ children }) {
  return <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 space-y-6">{children}</main>;
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 className="display text-2xl sm:text-[26px] font-semibold tracking-tight text-[#002060]">{title}</h1>
        {subtitle && <p className="text-sm text-[#002060]/60 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2 items-center">{actions}</div>}
    </div>
  );
}

// ============== KPI ==============
export function KpiGrid({ children }) {
  return <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{children}</div>;
}

export function Kpi({ label, value, delta, deltaTone = "neutral" }) {
  const tone = deltaTone === "up" ? "text-[#002060] bg-[#002060]/10" : deltaTone === "down" ? "text-[#FF6600] bg-[#FF6600]/10" : "text-[#002060]/70 bg-[#002060]/5";
  return (
    <div className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-[#002060]/60 font-medium">{label}</div>
      <div className="mt-2 flex items-baseline justify-between gap-2">
        <div className="display text-2xl font-semibold num text-[#002060] truncate">{value}</div>
        {delta != null && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${tone} num`}>{delta}</span>}
      </div>
    </div>
  );
}

// ============== Card / Section ==============
export function Card({ title, action, children, padded = true, className = "" }) {
  return (
    <section className={`bg-[#FFFFFF] border border-[#002060]/15 rounded-lg ${className}`}>
      {(title || action) && (
        <header className="px-4 sm:px-5 py-3 border-b border-[#002060]/15 flex items-center justify-between gap-3">
          {title && <h2 className="display text-sm font-semibold text-[#002060]">{title}</h2>}
          {action}
        </header>
      )}
      <div className={padded ? "p-4 sm:p-5" : ""}>{children}</div>
    </section>
  );
}

// ============== FilterBar ==============
export function FilterBar({ search, onSearch, placeholder = "Search…", filters, onExport, right }) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-[#FFFFFF] border border-[#002060]/15 rounded-lg px-3 py-2">
      <div className="relative flex-1 min-w-[180px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#002060]/40" />
        <Input
          data-testid="filter-search"
          value={search || ""}
          onChange={(e) => onSearch?.(e.target.value)}
          placeholder={placeholder}
          className="pl-8 h-9 bg-[#FFFFFF] border-[#002060]/15 focus-visible:ring-[#FF6600]"
        />
      </div>
      {filters && <div className="flex flex-wrap items-center gap-1.5">{filters}</div>}
      {right}
      {onExport && (
        <Button data-testid="filter-export" variant="outline" size="sm" onClick={onExport} className="h-9 border-[#002060]/30 text-[#002060]">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export
        </Button>
      )}
    </div>
  );
}

export function FilterChip({ label, value, options, onChange }) {
  return (
    <label className="relative inline-flex items-center text-xs">
      <span className="absolute left-2.5 text-[#002060]/60 pointer-events-none flex items-center gap-1">
        <FilterIcon className="w-3 h-3" /> {label}:
      </span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={`filter-${label.toLowerCase()}`}
        className="appearance-none h-9 pl-[68px] pr-8 bg-[#FFFFFF] border border-[#002060]/15 rounded-md text-xs text-[#002060] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6600]"
      >
        <option value="">all</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 w-3 h-3 text-[#002060]/40 pointer-events-none" />
    </label>
  );
}

// ============== Status Pill ==============
const STATUS_TONES = {
  // Pills designed to read on BOTH white (#FFFFFF) and navy (#002060) backgrounds.
  // Solid orange = action / in-progress; white outlined = stable / neutral.
  emerald: "bg-[#FFFFFF] text-[#002060] border-[#002060]",                       // completed / paid
  blue: "bg-[#FFFFFF] text-[#FF6600] border-[#FF6600]",                          // sent / scheduled
  amber: "bg-[#FF6600] text-[#FFFFFF] border-[#FF6600]",                         // active / en-route / on-site / draft
  rose: "bg-[#FFFFFF] text-[#FF6600] border-[#FF6600] line-through decoration-1", // void / cancelled
  slate: "bg-[#FFFFFF] text-[#002060] border-[#002060]/40",                      // neutral / new
  orange: "bg-[#FF6600] text-[#FFFFFF] border-[#FF6600]",                        // action needed
};
const JOB_STATUS_TONE = {
  new: "slate", scheduled: "blue", "en-route": "amber",
  "on-site": "amber", completed: "emerald", cancelled: "rose",
};
export function StatusPill({ status, tone, label }) {
  const resolved = tone || JOB_STATUS_TONE[status] || "slate";
  return (
    <span className={`inline-flex items-center text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_TONES[resolved]}`}>
      {label || status}
    </span>
  );
}

// ============== Table ==============
export function Table({ head, rows, empty = "Nothing here yet." }) {
  if (!rows || rows.length === 0) return <EmptyState message={empty} />;
  return (
    <div className="overflow-x-auto -mx-4 sm:-mx-5">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-[#002060]/60 border-b border-[#002060]/15">
            {head.map((h, i) => (
              <th key={i} className={`px-4 sm:px-5 py-2.5 font-medium ${h.align === "right" ? "text-right" : ""} ${h.className || ""}`}>{h.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

export function Tr({ children, onClick, testid }) {
  return (
    <tr
      onClick={onClick}
      data-testid={testid}
      className={`border-b border-[#002060]/10 last:border-b-0 ${onClick ? "cursor-pointer hover:bg-[#FF6600]/5" : ""} transition-colors`}
    >{children}</tr>
  );
}
export function Td({ children, align, className = "" }) {
  return <td className={`px-4 sm:px-5 py-3 ${align === "right" ? "text-right" : ""} ${className}`}>{children}</td>;
}

// ============== Empty / Loading ==============
export function EmptyState({ message = "Nothing here yet.", action, icon: Icon = Inbox, hint }) {
  return (
    <div className="py-12 text-center">
      <Icon className="w-8 h-8 text-[#002060]/30 mx-auto mb-3" />
      <p className="text-sm text-[#002060]/70 font-medium">{message}</p>
      {hint && <p className="text-xs text-[#002060]/50 mt-1">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Loading({ rows = 5 }) {
  return (
    <div className="space-y-2 py-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 bg-[#002060]/10 rounded animate-pulse" />
      ))}
    </div>
  );
}

// ============== Brand mark (logo porthole) ==============
export function BrandMark({ size = 32, label = "Space", sublabel }) {
  return (
    <div className="flex items-center gap-2.5 shrink-0">
      <span className="rounded-full bg-[#FFFFFF] grid place-items-center overflow-hidden border border-[#002060]/30" style={{ width: size, height: size }}>
        <img src="/brand/logo.png" alt="Space Appliance" className="w-[94%] h-[94%] object-contain" style={{ mixBlendMode: "multiply" }} />
      </span>
      <div className="hidden sm:block leading-tight">
        <div className="display text-sm font-semibold text-[#002060]">{label}</div>
        {sublabel && <div className="text-[10px] uppercase tracking-widest text-[#002060]/60">{sublabel}</div>}
      </div>
    </div>
  );
}

// ============== Helpers ==============
export function toCsv(rows, columns) {
  const head = columns.map((c) => c.label).join(",");
  const lines = rows.map((r) =>
    columns.map((c) => {
      const v = c.get(r);
      const s = v == null ? "" : String(v).replace(/"/g, '""');
      return /[,"\n]/.test(s) ? `"${s}"` : s;
    }).join(",")
  );
  return [head, ...lines].join("\n");
}

export function downloadCsv(filename, rows, columns) {
  const blob = new Blob([toCsv(rows, columns)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
